/**
 * Notification Context for HeatCraftHP
 * Provides centralized notification display using Ant Design
 * Listens to API events and displays appropriate notifications
 */

import React, { createContext, useContext, useEffect, useCallback, useState } from 'react';
import { App } from 'antd';
import { 
  ServerError, 
  NetworkError, 
  UnauthorizedError, 
  ForbiddenError,
  RateLimitError,
  ValidationError,
  isApiError 
} from '../services/apiErrors';

// Context for notification methods
const NotificationContext = createContext(null);

/**
 * Hook to access notification methods
 * @returns {{ showSuccess, showError, showWarning, showInfo, showApiError }}
 */
export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}

/**
 * Notification Provider Component
 * Wraps the app and provides notification methods
 */
export function NotificationProvider({ children }) {
  // Use Ant Design's App hooks for notifications
  const { notification, message } = App.useApp();
  
  // Track if re-auth is in progress to suppress 401 notifications
  const [isReAuthActive, setIsReAuthActive] = useState(false);
  
  // Listen for re-auth events to manage notification suppression
  useEffect(() => {
    const handleReAuthNeeded = () => {
      setIsReAuthActive(true);
    };
    
    const handleReAuthComplete = () => {
      setIsReAuthActive(false);
    };
    
    window.addEventListener('auth:reauth-needed', handleReAuthNeeded);
    window.addEventListener('auth:reauth-success', handleReAuthComplete);
    window.addEventListener('auth:reauth-cancelled', handleReAuthComplete);
    window.addEventListener('auth:logout', handleReAuthComplete);
    
    return () => {
      window.removeEventListener('auth:reauth-needed', handleReAuthNeeded);
      window.removeEventListener('auth:reauth-success', handleReAuthComplete);
      window.removeEventListener('auth:reauth-cancelled', handleReAuthComplete);
      window.removeEventListener('auth:logout', handleReAuthComplete);
    };
  }, []);

  /**
   * Show success notification
   */
  const showSuccess = useCallback((msg, description = null, duration = 4) => {
    if (description) {
      notification.success({
        message: msg,
        description,
        duration,
        placement: 'topRight',
      });
    } else {
      message.success(msg, duration);
    }
  }, [notification, message]);

  /**
   * Show error notification
   */
  const showError = useCallback((msg, description = null, duration = 6) => {
    if (description) {
      notification.error({
        message: msg,
        description,
        duration,
        placement: 'topRight',
      });
    } else {
      message.error(msg, duration);
    }
  }, [notification, message]);

  /**
   * Show warning notification
   */
  const showWarning = useCallback((msg, description = null, duration = 5) => {
    if (description) {
      notification.warning({
        message: msg,
        description,
        duration,
        placement: 'topRight',
      });
    } else {
      message.warning(msg, duration);
    }
  }, [notification, message]);

  /**
   * Show info notification
   */
  const showInfo = useCallback((msg, description = null, duration = 4) => {
    if (description) {
      notification.info({
        message: msg,
        description,
        duration,
        placement: 'topRight',
      });
    } else {
      message.info(msg, duration);
    }
  }, [notification, message]);

  /**
   * Show API error with appropriate formatting
   * Handles different error types specially
   */
  const showApiError = useCallback((error) => {
    if (!isApiError(error)) {
      // Generic JavaScript error
      showError('Error', error.message || 'An unexpected error occurred');
      return;
    }

    // Server errors (500s) - show correlation ID
    if (error instanceof ServerError) {
      notification.error({
        message: 'Server Error',
        description: (
          <div>
            <p>{error.message}</p>
            {error.correlationId && (
              <p style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>
                Reference ID: <code style={{ 
                  backgroundColor: '#f5f5f5', 
                  padding: '2px 6px', 
                  borderRadius: '4px',
                  fontFamily: 'monospace'
                }}>{error.correlationId}</code>
              </p>
            )}
          </div>
        ),
        duration: 10,
        placement: 'topRight',
      });
      return;
    }

    // Network errors
    if (error instanceof NetworkError) {
      notification.error({
        message: 'Connection Error',
        description: error.message,
        duration: 8,
        placement: 'topRight',
      });
      return;
    }

    // Unauthorized - session expired
    // Suppress this notification if re-auth modal is showing
    if (error instanceof UnauthorizedError) {
      if (!isReAuthActive) {
        notification.warning({
          message: 'Session Expired',
          description: 'Please log in again to continue.',
          duration: 6,
          placement: 'topRight',
        });
      }
      return;
    }

    // Forbidden - access denied
    // Suppress CSRF-related 403 errors if re-auth modal is showing
    if (error instanceof ForbiddenError) {
      const isCsrfError = error.message?.toLowerCase().includes('csrf');
      if (isCsrfError && isReAuthActive) {
        // Don't show notification - re-auth modal is handling this
        return;
      }
      notification.error({
        message: 'Access Denied',
        description: error.message,
        duration: 6,
        placement: 'topRight',
      });
      return;
    }

    // Rate limited
    if (error instanceof RateLimitError) {
      notification.warning({
        message: 'Too Many Requests',
        description: `${error.message} Please wait ${error.retryAfter} seconds before trying again.`,
        duration: error.retryAfter,
        placement: 'topRight',
      });
      return;
    }

    // Validation errors (400s) - show the controller message
    if (error instanceof ValidationError) {
      // If there are field-specific errors, list them
      if (Object.keys(error.validationErrors).length > 0) {
        notification.error({
          message: 'Validation Error',
          description: (
            <div>
              <p>{error.message}</p>
              <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                {Object.entries(error.validationErrors).map(([field, messages]) => (
                  <li key={field}>{messages.join(', ')}</li>
                ))}
              </ul>
            </div>
          ),
          duration: 8,
          placement: 'topRight',
        });
      } else {
        // Just show the main message from controller
        message.error(error.message, 6);
      }
      return;
    }

    // Default - use the message from the controller/backend
    message.error(error.message, 6);
  }, [notification, message, showError, isReAuthActive]);

  /**
   * Listen to API events from apiClient
   */
  useEffect(() => {
    const handleApiError = (event) => {
      const { error, options } = event.detail;
      if (!options?.suppressNotification) {
        showApiError(error);
      }
    };

    const handleApiSuccess = (event) => {
      const { message: msg } = event.detail;
      if (msg) {
        showSuccess(msg);
      }
    };

    window.addEventListener('api:error', handleApiError);
    window.addEventListener('api:success', handleApiSuccess);

    return () => {
      window.removeEventListener('api:error', handleApiError);
      window.removeEventListener('api:success', handleApiSuccess);
    };
  }, [showApiError, showSuccess]);

  const contextValue = {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showApiError,
    // Expose raw Ant Design APIs for advanced use
    notification,
    message,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
}

/**
 * HOC to wrap a component with notification context
 */
export function withNotification(Component) {
  return function NotificationWrappedComponent(props) {
    const notification = useNotification();
    return <Component {...props} notification={notification} />;
  };
}

export default NotificationContext;
